import { AlertCircle } from "lucide-react";
import * as React from "react";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/lib/utils";
import { Label } from "@/shared/components/primitives/label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id, required } = itemContext;

  return {
    id,
    required,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
  /**
   * Marks the field as required: `FormLabel` renders the asterisk and `FormControl` sets `aria-required`.
   * Deliberately NOT the native `required` attribute — that would hand validation to the browser and race the
   * Zod resolver that actually owns it here.
   */
  required?: boolean;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, required, ...props }: React.ComponentProps<"div"> & { required?: boolean }) {
  const id = React.useId();
  const context = React.useMemo(() => ({ id, required }), [id, required]);

  return (
    <FormItemContext.Provider value={context}>
      <div data-slot="form-item" className={cn("group/field grid gap-2", className)} {...props} />
    </FormItemContext.Provider>
  );
}

function FormLabel({ className, ...props }: React.ComponentProps<"label">) {
  const { error, formItemId, required } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn(
        "data-[error=true]:text-destructive group-data-[disabled=true]/field:cursor-not-allowed group-data-[disabled=true]/field:opacity-50",
        // The required mark is generated content, NOT a child node. As a `<span>` inside the label it
        // became part of the label's text, which is what `getByLabelText` matches on — it compares the
        // label's text content and so ignores `aria-hidden`. Every exact label query in the app, and any
        // tool reading the label as a string, would have started seeing "Tipo de documento*". The control
        // carries `aria-required`, so nothing is lost by making this purely visual.
        // `-ml-1.5` cancels most of the label's `gap-2`: a pseudo-element is a flex item too, and 8px away
        // the asterisk reads as its own word rather than a mark on the label.
        required && "after:-ml-1.5 after:text-destructive after:content-['*']",
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
}

type FormControlChild = React.ReactElement<
  React.HTMLAttributes<HTMLElement> & Record<`data-${string}`, string | boolean | undefined>
>;

function FormControl({ children }: { children: FormControlChild }) {
  const { error, formItemId, formDescriptionId, formMessageId, required } = useFormField();

  return React.cloneElement(children, {
    "data-slot": "form-control",
    id: formItemId,
    "aria-describedby": !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`,
    "aria-invalid": !!error,
    // Advisory only, unlike the native attribute: it tells assistive tech the field is required without
    // switching on browser validation. `undefined` rather than `false` so nothing is announced otherwise.
    "aria-required": required || undefined,
  });
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground group-data-[disabled=true]/field:opacity-50", className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;

  if (!body) {
    return null;
  }

  return (
    <div className="flex gap-1 text-destructive">
      <AlertCircle size={14} className="text-current shrink-0 mt-1" />
      <div>
        <p data-slot="form-message" id={formMessageId} className={cn("text-sm", className)} {...props}>
          {body}
        </p>
      </div>
    </div>
  );
}

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };
